import CommonCss from "nlib/common/common.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getDocumentsStats, getUploadingDocumentsCount } from "selectors/documents";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import Constants from "const/Constants";
import DocumentFileTypes from "const/DocumentFileTypes";
import DocumentsStatusFilter from "nlib/pages/DocumentsPage/lib/DocumentsStatusFilter";
import Filters from "./lib/Filters";
import ListItem from "./lib/ListItem";
import ListItemDetails from "./lib/ListItemDetails";
import NoDataContent from "nlib/common/NoDataContent";
import PageActionButtons from "mlib/common/PageActionButtons";
import Pagination from "nlib/ui/Pagination";
import Platforms from "utils/Platforms";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useMemo } from "react";
import UploadDocumentsModal from "./lib/UploadDocumentsModal";
import UploadFilesButton from "nlib/ui/UploadFilesButton";
import classNames from "classnames";
import useDocuments from "hooks/useDocuments";
import useEnvVars from "hooks/useEnvVars";
import useShowModal from "hooks/useShowModal";
import useUploadDocuments from "hooks/useUploadDocuments";

const FILE_DROP_ALLOWED_TYPES = Object.values(DocumentFileTypes)
  .reduce((aggregator, { extensions, mimeType }) => [...aggregator, ...extensions, mimeType], []);

const MAX_DISPLAYED_PAGES = 5;

const DocumentsPage = () => {
  const [envVars, setEnvVars] = useEnvVars();

  const {
    status,
    page = 1,
    limit = Constants.TABLE_PAGE_SIZE,
    editItem: editItemFallback,
    documentId: editItem = editItemFallback
  } = envVars;

  const {
    fetching: documentsFetching,
    data: documentsData,
    refetch: refetchDocuments
  } = useDocuments();

  const { uiTexts } = useSelector(getTextsData);

  const documentsStats = useSelector(getDocumentsStats);

  const uploadDocuments = useUploadDocuments();

  const [
    uploadDocumentsModalData,
    showUploadDocumentsModal,
    onUploadDocumentsModalClose
  ] = useShowModal();

  const documentsCurrentUploadCount = useSelector(getUploadingDocumentsCount);

  const [editItemId] = editItem ? editItem.split(".") : [];

  const editDocumentData = Arrays.findById(documentsData, editItemId);

  const totalCount = useMemo(() => {
    if (status === "paid") {
      return documentsStats.exportedPaid;
    }
    if (status === "unpaid") {
      return documentsStats.exportedUnpaid;
    }

    return documentsStats.current;
  }, [documentsStats, status]);

  const handlePageChange = useCallback((nextPage) => {
    setEnvVars({ page: nextPage });
  }, [setEnvVars]);

  const handleListItemClick = useCallback((itemId) => {
    setEnvVars({ documentId: itemId });
  }, [setEnvVars]);

  const handleItemDetailClose = useCallback(() => {
    setEnvVars({ documentId: null, editItem: null });
  }, [setEnvVars]);

  const handleFilesChange = useCallback(async(files) => {
    if (!files) return;

    const modalResult = await showUploadDocumentsModal({ files });

    if (modalResult) {
      const successful = await uploadDocuments(modalResult);

      if (successful) refetchDocuments();
    }
  }, [showUploadDocumentsModal, uploadDocuments, refetchDocuments]);

  useEffect(() => {
    if (documentsFetching
      || documentsData.length
      || !totalCount) {
      return;
    }
    setEnvVars({ page: Math.ceil(totalCount / limit) });
  }, [
    page,
    limit,
    documentsFetching,
    documentsData.length,
    totalCount,
    setEnvVars
  ]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, status]);
  useEffect(() => {
    if (!documentsFetching && editItemId && !editDocumentData && !envVars.text) {
      setEnvVars({ text: editItemId });
    }
  }, [documentsFetching, editDocumentData, editItemId, envVars.text, setEnvVars]);

  return (
    <>
      <Filters />
      <div className={Css.documentsPage}>
        <div className={CommonCss.mobileContainer}>
          <DocumentsStatusFilter className={Css.statusFilter} />
        </div>
        <div className={classNames(Css.content, CommonCss.mobileContainer)}>
          {documentsData.length
            ? Arrays.sort(documentsData, "status", true).map((documentData) => (
              <ListItem
                disabled={documentsFetching}
                key={documentData.id}
                data={documentData}
                onItemClick={handleListItemClick} />
            ))
            : (
              documentsFetching
                ? <Preloader />
                : <NoDataContent title={uiTexts.noDocuments} />
            )}
        </div>
        <PageActionButtons>
          {Platforms.checkIsNativeAndroid() && (
            <UploadFilesButton
              large outline
              data-loading={documentsCurrentUploadCount ? "" : undefined}
              disabled={documentsFetching || documentsCurrentUploadCount}
              icon={documentsCurrentUploadCount ? Icons.Spinner : Icons.Camera}
              capture="environment"
              accept={["image/*"]}
              onChange={handleFilesChange}>
              {uiTexts.takePhoto}
            </UploadFilesButton>
          )}
          <UploadFilesButton
            primary multiple
            data-loading={documentsCurrentUploadCount ? "" : undefined}
            disabled={documentsFetching || documentsCurrentUploadCount}
            icon={documentsCurrentUploadCount ? Icons.Spinner : Icons.FilePlus}
            accept={FILE_DROP_ALLOWED_TYPES}
            onChange={handleFilesChange}>
            {uiTexts.uploadDocuments}
          </UploadFilesButton>
        </PageActionButtons>
        {(totalCount > Number(limit)) && (
          <Pagination
            showFirstLast={false}
            className={Css.pagination}
            count={totalCount}
            page={Number(page)}
            maxDisplayedPages={MAX_DISPLAYED_PAGES}
            pageSize={Number(limit)}
            disabled={documentsFetching}
            onChange={handlePageChange} />
        )}
        {editItemId && (
          <ListItemDetails
            documentId={editItemId}
            data={editDocumentData}
            onClose={handleItemDetailClose} />
        )}
        {uploadDocumentsModalData && (
          <UploadDocumentsModal
            data={uploadDocumentsModalData}
            onClose={onUploadDocumentsModalClose} />
        )}
      </div>
    </>
  );
};

export default React.memo(DocumentsPage);
